import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Type,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Select,
  ValidationMessaging,
  Spinner,
} from "discover.medical.react-component-library";
import {
  StyledMessagesContainer,
  StyledFieldsContainer,
  StyledField,
  StyledInputText,
  StyledTextContainer,
} from "./CollectUserDataDialog.styled";
import { useDispatch, useStore } from "../../store";
import { updateUserAttributes } from "../../store/actions/personalization";

const validationSchema = Yup.object().shape({
  givenName: Yup.string().required("First name is required."),
  familyName: Yup.string().required("Last name is required."),
  primaryEmail: Yup.string()
    .required("Email is required.")
    .matches(
      /^(?!(?:changeme@ebsco\.com|placeholder@email\.com)$)[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      "Please enter a valid email address."
    ),
  specialty: Yup.string().required("Specialty is required."),
  role: Yup.string().required("Role is required."),
});

export default function CollectUserDataDialog({
  attributes,
  roles,
  specialties,
}) {
  const { givenName, familyName, primaryEmail, role, specialty } = attributes;
  const [isOpened, setIsOpened] = useState(true);
  const [submitPressed, setSubmitPressed] = useState(false);
  const dispatch = useDispatch();
  const {
    personalization: { isUpdating },
  } = useStore();

  const inputProps = [
    { name: "givenName", label: "First Name", dataExists: !!givenName?.trim() },
    {
      name: "familyName",
      label: "Last Name",
      dataExists: !!familyName?.trim(),
    },
    {
      name: "primaryEmail",
      label: "Email",
      dataExists: !!primaryEmail?.trim(),
    },
  ];

  const selectProps = [
    {
      name: "specialty",
      label: "Specialty",
      dataExists: !!specialty?.trim(),
      optionsArray: specialties,
    },
    {
      name: "role",
      label: "Role",
      dataExists: !!role?.trim(),
      optionsArray: roles,
    },
  ];

  const formik = useFormik({
    initialValues: {
      givenName: givenName?.trim() || "",
      familyName: familyName?.trim() || "",
      primaryEmail: primaryEmail?.trim() || "",
      role: role?.trim() || "",
      specialty: specialty?.trim() || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const dataToUpdate = {
        ...attributes,
        givenName: givenName?.trim() || values.givenName,
        familyName: familyName?.trim() || values.familyName,
        primaryEmail: primaryEmail?.trim() || values.primaryEmail,
        role: role?.trim() || values.role,
        specialty: specialty?.trim() || values.specialty,
      };
      await updateUserAttributes(dispatch, dataToUpdate);
      setIsOpened(false);
    },
  });

  const renderText = ({ label, name }) => (
    <StyledTextContainer
      key={name}
      element="div"
      marginTop="md"
      data-testid="collect-user-data-text-container"
    >
      <Type element="span" fontWeight="bold" marginBottom="xs">
        {label}
      </Type>
      <Type element="span">{formik.values[name]}</Type>
    </StyledTextContainer>
  );

  const renderInput = ({ name, label, dataExists }) =>
    dataExists ? (
      renderText({ label, name })
    ) : (
      <StyledField
        key={name}
        id={name}
        label={label}
        message={
          <ValidationMessaging
            isHidden={!submitPressed}
            hasError={!!(formik.touched[name] && formik.errors[name])}
          >
            {formik.errors[name]}
          </ValidationMessaging>
        }
      >
        <StyledInputText
          name={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
          hasError={!!(formik.touched[name] && formik.errors[name])}
          placeholder={`Enter ${label}`}
        />
      </StyledField>
    );

  const renderSelect = ({ name, label, dataExists, optionsArray }) =>
    dataExists ? (
      renderText({ label, name })
    ) : (
      <StyledField
        key={name}
        id={name}
        label={label}
        message={
          <ValidationMessaging
            isHidden={!submitPressed}
            hasError={!!(formik.touched[name] && formik.errors[name])}
          >
            {formik.errors[name]}
          </ValidationMessaging>
        }
      >
        <Select
          id={name}
          label={label}
          name={name}
          value={formik.values[name]}
          onChange={formik.handleChange}
          hasError={!!(formik.touched[name] && formik.errors[name])}
        >
          <Select.Option value="">{`Select ${label}`}</Select.Option>
          {optionsArray.map((item) => (
            <Select.Option key={item.replace(" ", "-")} value={item}>
              {item}
            </Select.Option>
          ))}
        </Select>
      </StyledField>
    );

  return (
    <Dialog
      isOpen={isOpened}
      maxWidth="sm"
      data-testid="collect-user-data-dialog"
    >
      <DialogTitle
        borderBottomColor="gray300"
        borderBottomStyle="solid"
        borderBottomWidth="1px"
        element="h2"
        styledAs="h3"
        color="gray800"
      >
        Please provide the information requested below so we may complete your
        account setup.
      </DialogTitle>
      <StyledMessagesContainer>
        <Type element="i" fontSize="sm">
          We will keep your information secure and not request it again. Thank
          you for your cooperation.
        </Type>
        {isUpdating && <Spinner size="lg" />}
        {submitPressed && !!Object.keys(formik.errors).length && (
          <Type element="strong" fontSize="sm" color="error" marginTop="md">
            Please correct any errors noted below.
          </Type>
        )}
      </StyledMessagesContainer>
      <form
        onSubmit={(e) => {
          setSubmitPressed(true);
          formik.handleSubmit(e);
        }}
      >
        <StyledFieldsContainer>
          {inputProps.map((props) => renderInput(props))}
          {selectProps.map((props) => renderSelect(props))}
        </StyledFieldsContainer>
        <DialogActions justify="center">
          <Button type="submit" disabled={isUpdating}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
